import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import AuthImage from "./AuthImage";

/**
 * Infinite carousel lightbox for lot photos.
 * - Loops on next/prev at either end
 * - Escape closes, Left/Right arrows navigate
 * - Renders via portal so it sits above modals/print areas cleanly
 * - Fully responsive: fills the viewport on mobile, keeps aspect on desktop
 */
export default function Lightbox({ photos, startIndex = 0, onClose, title }) {
  const [idx, setIdx] = useState(startIndex);
  const count = photos?.length || 0;

  const next = useCallback(() => setIdx((i) => (count ? (i + 1) % count : 0)), [count]);
  const prev = useCallback(() => setIdx((i) => (count ? (i - 1 + count) % count : 0)), [count]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, next, prev]);

  if (!count) return null;
  const photo = photos[idx];
  const content = (
    <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col" data-testid="lightbox"
      onClick={onClose} role="dialog" aria-modal="true">
      <div className="flex items-center justify-between px-4 py-3 text-white flex-shrink-0"
        onClick={(e) => e.stopPropagation()}>
        <div className="text-xs uppercase tracking-widest font-mono2 truncate">
          {title || "Photo"} · {idx + 1} / {count}
          {photo?.label && <span className="ml-3 text-white/70">{photo.label}</span>}
        </div>
        <button onClick={onClose} data-testid="lightbox-close"
          className="p-2 text-white/80 hover:text-white">
          <X size={20}/>
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center relative min-h-0"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={prev} data-testid="lightbox-prev"
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center z-10">
          <ChevronLeft size={24}/>
        </button>
        <div className="max-h-full max-w-full flex items-center justify-center p-4 md:p-10">
          <AuthImage path={photo?.storage_path}
            className="max-h-[80vh] max-w-full object-contain shadow-2xl" />
        </div>
        <button onClick={next} data-testid="lightbox-next"
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center z-10">
          <ChevronRight size={24}/>
        </button>
      </div>
      {count > 1 && (
        <div className="flex justify-center gap-2 pb-4 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}>
          {photos.map((p, i) => (
            <button key={p.storage_path || i} onClick={() => setIdx(i)}
              data-testid={`lightbox-dot-${i}`}
              className={`w-2 h-2 rounded-full ${i === idx ? "bg-white" : "bg-white/40"}`} />
          ))}
        </div>
      )}
    </div>
  );
  return createPortal(content, document.body);
}
